import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import {
  PutRequest,
  useCustomersServiceObjectPutMutation,
} from "@/services/backend/customers/api/service-object-api";
import { useCallback, useEffect, useState } from "react";
import { v4 } from "uuid";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { newAddress } from "@/services/backend/addresses/types/address";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInput } from "@/shared/components/form/text-input";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { Label } from "@/shared/components/ui/label";
import { AddressFields } from "@/services/backend/addresses/components/address-card";
import { toast } from "sonner";
import { Separator } from "@/shared/components/ui/separator";

export function CreateServiceObjectDialog({
  customerId,
}: {
  customerId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(newRequest(customerId));
  const [put, { isLoading, error, isSuccess, reset }] =
    useCustomersServiceObjectPutMutation();

  const handleOpenChange = useCallback(
    (value: boolean) => {
      setOpen(value);
      reset();
    },
    [reset],
  );

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      setRequest(newRequest(customerId));
      reset();
      toast("Service Objekt erstellt");
    }
  }, [customerId, handleOpenChange, isSuccess, reset]);

  const doCreate = () => {
    if (isLoading) {
      return;
    }

    put(request);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <CommonDialogTrigger icon={Plus} label={t("Service Objekt")} size="sm" />
      <DialogContent>
        <CommonDialogHeader
          title={t("Service Objekt erstellen")}
          description={t("Hier kann ein Service Objekt erstellt werden.")}
        />
        <Fields request={request} onRequestChange={setRequest} error={error} />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          actionLabel={t("Erstellen")}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}

function Fields({
  request,
  onRequestChange,
  error,
}: {
  request: PutRequest;
  onRequestChange: (request: PutRequest) => void;
  error: unknown;
}) {
  return (
    <div className="grid gap-4">
      <TextInput
        label={t("Name")}
        field="name"
        text={request.name}
        onTextChange={(name) => onRequestChange({ ...request, name })}
        errors={inputValidationErrorMessages(error, "name")}
      />
      <div className="grid gap-1.5">
        <Label>{t("Kunde")}</Label>
        <CustomerSelect
          value={request.customerId}
          onValueChange={(customerId) =>
            onRequestChange({ ...request, customerId })
          }
        />
        <InputValidationErrors error={error} field="customerId" />
      </div>
      <Separator />
      <div className="font-medium">{t("Adresse")}</div>
      <AddressFields
        address={request.address}
        onAddressChange={(address) => onRequestChange({ ...request, address })}
      />
    </div>
  );
}

function newRequest(customerId?: string): PutRequest {
  return {
    id: v4(),
    customerId: customerId ?? NullUUID,
    name: "",
    address: newAddress(),
  };
}
